import type {
    TradingHoursRecordBlacklistResult,
    TradingHoursRowCollectionName,
    TradingHoursUnfreezeResult,
} from '@thinkalpha/quant-ws-client/contracts/trading-hours-admin.js';

export type TradingHoursTab = 'status' | 'versions' | 'overrides' | 'holidays' | 'history' | 'diff';

export const TRADING_HOURS_TABS: readonly TradingHoursTab[] = [
    'status',
    'versions',
    'overrides',
    'holidays',
    'history',
    'diff',
];

/** Simple levers that need at most a confirmation and an optional reason. */
export type ConfirmableAction =
    | { kind: 'blacklist-version'; ingestionId: string; blacklisted: boolean }
    | { kind: 'release-override'; overrideId: string; label: string }
    | {
          kind: 'record-blacklist';
          collection: TradingHoursRowCollectionName;
          ids: readonly string[];
          blacklisted: boolean;
          label: string;
      };

export type PendingAction =
    | { kind: 'confirm'; action: ConfirmableAction; reason: string; busy: boolean }
    | {
          kind: 'freeze';
          blacklistCurrent: boolean;
          pinTo: string | null;
          reason: string;
          /** The effective version when the dialog opened; a blacklist is refused if it moved since. */
          effectiveId: string | null;
          busy: boolean;
      }
    | {
          kind: 'unfreeze';
          /** `null` while the preview is loading. */
          preview: TradingHoursUnfreezeResult | null;
          busy: boolean;
      };

export interface RecordBlacklistForm {
    collection: TradingHoursRowCollectionName;
    /** Raw textarea content; ids are split on whitespace and commas at submit. */
    idsText: string;
    blacklisted: boolean;
    reason: string;
}

export interface HolidayLookup {
    /** ISO date, `YYYY-MM-DD`. */
    date: string;
    /** A MIC or a vendor FinID; FinIDs contain a dot (`US.NYSE`). */
    market: string;
}

export interface DiffSelection {
    from: string;
    to: string;
}

export interface TradingHoursAdminState {
    tab: TradingHoursTab;
    versionsLimit: number;
    selectedVersionId: string | null;
    pendingAction: PendingAction | null;
    recordForm: RecordBlacklistForm;
    recordResult: TradingHoursRecordBlacklistResult | null;
    holidayDraft: HolidayLookup;
    /** The lookup the query runs for; `null` until submitted. */
    holidayLookup: HolidayLookup | null;
    historyVersionId: string | null;
    historyLimit: number;
    diffDraft: DiffSelection;
    /** The pair the diff query runs for; `null` until submitted. */
    diffSelection: DiffSelection | null;
}

export const DEFAULT_VERSIONS_LIMIT = 50;
export const DEFAULT_HISTORY_LIMIT = 200;

export const initialTradingHoursAdminState: TradingHoursAdminState = {
    tab: 'status',
    versionsLimit: DEFAULT_VERSIONS_LIMIT,
    selectedVersionId: null,
    pendingAction: null,
    recordForm: { collection: 'holidays', idsText: '', blacklisted: true, reason: '' },
    recordResult: null,
    holidayDraft: { date: '', market: '' },
    holidayLookup: null,
    historyVersionId: null,
    historyLimit: DEFAULT_HISTORY_LIMIT,
    diffDraft: { from: '', to: '' },
    diffSelection: null,
};
